import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "./api";

/**
 * Lazy-load the Spotify Web Playback SDK and bootstrap a single browser-side
 * Spotify Connect device.
 *
 * Returns:
 *   ready        — true once the SDK has connected and produced a deviceId
 *   deviceId     — the in-browser Connect device id (use it when calling
 *                  POST /spotify/play to direct playback here)
 *   state        — Spotify Player state ({track_window, paused, position,
 *                  duration, repeat_mode, shuffle, ...}) or null
 *   controls     — { play, pause, togglePlay, next, prev, seek, setVolume }
 *   error        — string | null  (non-Premium accounts, init failures)
 *
 * Premium gating: Spotify's SDK fires a `account_error` event for free
 * accounts. We expose this via `error` so callers can render an upgrade UI.
 *
 * Token refresh: `getOAuthToken(cb)` hits our backend /spotify/access-token,
 * which uses the stored refresh_token to mint a fresh access_token. Refresh
 * tokens never leave the server.
 */
const SDK_SRC = "https://sdk.scdn.co/spotify-player.js";

const loadSDK = () =>
  new Promise((resolve, reject) => {
    if (window.Spotify) {
      resolve(window.Spotify);
      return;
    }
    if (document.querySelector(`script[src="${SDK_SRC}"]`)) {
      // Script already in flight — wait for the ready callback to fire.
      const prev = window.onSpotifyWebPlaybackSDKReady;
      window.onSpotifyWebPlaybackSDKReady = () => {
        prev?.();
        resolve(window.Spotify);
      };
      return;
    }
    window.onSpotifyWebPlaybackSDKReady = () => resolve(window.Spotify);
    const s = document.createElement("script");
    s.src = SDK_SRC;
    s.async = true;
    s.onerror = reject;
    document.body.appendChild(s);
  });

export function useSpotifySDK({ enabled, onReady } = {}) {
  const [ready, setReady] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const playerRef = useRef(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let player = null;
    (async () => {
      try {
        const Spotify = await loadSDK();
        if (cancelled) return;
        player = new Spotify.Player({
          name: "DAIONE Web Player",
          volume: 0.6,
          getOAuthToken: async (cb) => {
            try {
              const { data } = await api.get("/spotify/access-token");
              cb(data.access_token);
            } catch (err) {
              console.warn("Spotify token refresh failed", err);
              cb(""); // SDK will surface authentication_error
            }
          },
        });
        playerRef.current = player;

        player.addListener("initialization_error", ({ message }) => setError(message));
        player.addListener("authentication_error", ({ message }) => setError(message));
        player.addListener("account_error", ({ message }) =>
          setError(
            message?.toLowerCase().includes("premium")
              ? "Spotify Premium is required to use the in-app player."
              : message
          )
        );
        player.addListener("playback_error", ({ message }) => console.warn("Spotify playback_error", message));

        player.addListener("ready", async ({ device_id }) => {
          if (cancelled) return;
          setDeviceId(device_id);
          setReady(true);
          setError(null);
          // Transfer playback to this device so /spotify/play targets us by default.
          try {
            await api.post("/spotify/transfer", null, { params: { device_id, play: false } });
          } catch (err) {
            console.warn("Spotify transfer failed", err);
          }
          onReadyRef.current?.(device_id);
        });
        player.addListener("not_ready", () => setReady(false));
        player.addListener("player_state_changed", (next) => setState(next));

        await player.connect();
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not initialise Spotify SDK");
      }
    })();

    return () => {
      cancelled = true;
      try {
        player?.disconnect();
      } catch (err) {
        // Disconnect occasionally throws if the SDK already tore down its
        // socket — non-fatal, log for visibility but don't surface to user.
        console.debug("Spotify player.disconnect() failed", err);
      }
      playerRef.current = null;
    };
  }, [enabled]);

  // --- controls ---
  const togglePlay = useCallback(async () => {
    try {
      await playerRef.current?.togglePlay();
    } catch (err) {
      console.warn("togglePlay failed", err);
    }
  }, []);

  const play = useCallback(
    async ({ uri, context_uri, position_ms = 0 }) => {
      if (!deviceId) return;
      const body = { device_id: deviceId };
      if (context_uri) body.context_uri = context_uri;
      else if (uri && (uri.startsWith("spotify:playlist:") || uri.startsWith("spotify:album:"))) {
        body.context_uri = uri;
      } else if (uri) {
        body.uri = uri;
      }
      if (position_ms) body.position_ms = position_ms;
      await api.post("/spotify/play", body);
    },
    [deviceId]
  );

  const pause = useCallback(() => playerRef.current?.pause(), []);
  const resume = useCallback(() => playerRef.current?.resume(), []);
  const next = useCallback(() => playerRef.current?.nextTrack(), []);
  const prev = useCallback(() => playerRef.current?.previousTrack(), []);
  const seek = useCallback((ms) => playerRef.current?.seek(ms), []);
  const setVolume = useCallback((v01) => playerRef.current?.setVolume(v01), []);
  const activateElement = useCallback(async () => {
    try {
      await playerRef.current?.activateElement?.();
    } catch (err) {
      // iOS requires this on first interaction; older SDKs lack the method.
      console.debug("Spotify activateElement failed", err);
    }
  }, []);

  return {
    ready,
    deviceId,
    state,
    error,
    controls: { play, pause, resume, togglePlay, next, prev, seek, setVolume, activateElement },
  };
}
